import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import WorkerHeader from "../../worker-header/components";
import WorkerFooter from "../../worker-footer/components";
import { getAllPlans } from "../slice/workerAllPlansSlice";
import { useLocation, useNavigate } from "react-router-dom";
import "../style/style.css";

const BACKEND_URL = process.env.REACT_APP_LARAVEL_API;

const WorkerAllPlans = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { workerAllPlansData } = useSelector(
    (state) => state.workerAllPlansSlice,
  );

  const token = localStorage.getItem("token");
  const [loadingPlanId, setLoadingPlanId] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // ✅ get plan_id from query
  const queryParams = new URLSearchParams(location.search);
  const planIdFromURL = queryParams.get("plan_id");

  useEffect(() => {
    // 🚨 If not logged in → redirect to signin with same plan_id
    if (!token) {
      if (planIdFromURL) {
        navigate(`/signin?plan_id=${planIdFromURL}`);
      } else {
        navigate("/signin");
      }
      return;
    }

    dispatch(getAllPlans(token));
  }, [token, dispatch, navigate, planIdFromURL]);

  // ✅ Auto trigger payment if plan_id exists
  useEffect(() => {
    if (token && planIdFromURL && workerAllPlansData.length > 0) {
      const selectedPlan = workerAllPlansData.find(
        (p) => p.id === Number(planIdFromURL),
      );

      if (selectedPlan) {
        setIsRedirecting(true); // show loader first

        setTimeout(() => {
          handlePlanPayment({ plan_id: selectedPlan.id });
        }, 300); // ⏳ give time to render
      }
    }
  }, [token, planIdFromURL, workerAllPlansData]);

  const handlePlanPayment = async (payload) => {
    try {
      setLoadingPlanId(payload.plan_id);
      const res = await fetch(`${BACKEND_URL}/checkout-session`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        console.error("Backend error:", data);
        setLoadingPlanId(null);
        return;
      }

      if (data?.data?.url) {
        window.location.href = data.data.url;
      }
    } catch (err) {
      setLoadingPlanId(null);
      console.error("Stripe Checkout error:", err);
    }
  };

  const features = [
    {
      title: "Strengthen Workforce Readiness",
      desc: "Identify gaps early and stay ahead of requirements.",
      icon: (
        // 🛡️ Shield
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"
            stroke="#0B3C8C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )
    },
    {
      title: "Simplify Onboarding & Documentation",
      desc: "Structured workflows save time and reduce errors.",
      icon: (
        // 👥 Users / Employees
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
          <circle
            cx="14"
            cy="10"
            r="5"
            stroke="#0B3C8C"
            strokeWidth="1.8"
          />
          <circle
            cx="6"
            cy="12"
            r="3.5"
            stroke="#0B3C8C"
            strokeWidth="1.5"
          />
          <circle
            cx="22"
            cy="12"
            r="3.5"
            stroke="#0B3C8C"
            strokeWidth="1.5"
          />
          <path
            d="M1 24c0-3.3 2.2-5 5-5"
            stroke="#0B3C8C"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M27 24c0-3.3-2.2-5-5-5"
            stroke="#0B3C8C"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M5 24c0-4.4 4-8 9-8s9 3.6 9 8"
            stroke="#0B3C8C"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      )
    },
    {
      title: "Navigate State Requirements",
      desc: "Find the right resources and official guidance—fast.",
      icon: (
        // 🏛️ Government / Building
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M3 10h18M5 10v8M9 10v8M15 10v8M19 10v8M2 18h20M12 3l10 7H2l10-7z"
            stroke="#0B3C8C" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )
    },
    {
      title: "Secure. Compliant. Built for Trust.",
      desc: "Enterprise-grade security to protect your data.",
      icon: (
        // 🔒 Lock
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect x="5" y="11" width="14" height="10" rx="2"
            stroke="#0B3C8C" strokeWidth="2" />
          <path d="M8 11V7a4 4 0 118 0v4"
            stroke="#0B3C8C" strokeWidth="2" />
        </svg>
      )
    }
  ];

  return (
    <>
      <div className={isRedirecting ? "blurred-content" : ""}>
        <WorkerHeader />

        <section className="ncaf-header-section">
          <div className="ncaf-header-inner">
            {/* TOP LOGO + TITLE ROW */}
            <div className="ncaf-title-block">
              <h1 className="ncaf-main-title">NCAF SUBSCRIPTIONS</h1>
              <p className="ncaf-subtitle">
                Workforce Infrastructure.&nbsp;&nbsp;Compliance
                Readiness.&nbsp;&nbsp;State Intelligence.
              </p>
            </div>

            {/* SHIELD DIVIDER */}
            <div className="ncaf-shield-divider">
              <div className="shield-line" />
              <div className="shield-icon">
                <svg
                  width="22"
                  height="26"
                  viewBox="0 0 22 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11 1L2 5V12C2 17.5 5.9 22.7 11 24C16.1 22.7 20 17.5 20 12V5L11 1Z"
                    stroke="#3B5EA6"
                    strokeWidth="1.5"
                    fill="none"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7.5 13l2.5 2.5L14.5 10"
                    stroke="#3B5EA6"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="shield-line" />
            </div>

            {/* DESCRIPTION */}
            <div className="ncaf-desc-block">
              <p className="ncaf-desc-text">
                Purpose-built for childcare providers, directors, and
                organizations.
              </p>
              <p className="ncaf-desc-text">
                Start free.&nbsp; Scale with confidence.
              </p>
            </div>
          </div>
        </section>

        {/* <section className="home-banner position-relative">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div
                  className="hero-section row align-items-center"
                  style={{ background: "#012c47" }}
                >
                  
                  <div className="col-lg-7 col-xl-7">
                    <div className="homebanner text-center text-lg-start">
                      <h1 className="montserrat font-weight-bold lh-1">
                        <p className="d-lg-block font-size-36 mb-0">
                          Membership Plans
                        </p>
                      </h1>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        {/* <section className="homepad" id="drive">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6">
                <div className="position-relative">
                  <img
                    src="/assets/images/about-img.jpg"
                    alt="..."
                    className="w-100 about-img"
                  />
                </div>
              </div>
              <div className="col-lg-6 font-size-16 font-weight-normal line-hight-3 text-muted mt-4 mt-lg-0 pt-3 pt-lg-0">
                <div className="ps-lg-4 ps-xl-5">
                  <div>
                    <div className="bg-primary-heading-main font-weight-semibold font-size-18 text-capitalize p-1 rounded-pill d-inline-block text-white pe-4">
                      <span className="bg-yellow d-inline-block p-1 bg-primary-heading-main rounded-pill favicon-logo01 text-center me-2">
                        <img
                          src="/assets/images/favicon-logo01.svg"
                          alt="..."
                          className="rounded-circle"
                        />
                      </span>
                      What Drives Us
                    </div>
                  </div>

                  <p className="mt-3">
                    <strong>
                      <span className="me-2 text-primary">✔</span> Early
                      Identification:
                    </strong>
                    <br />
                    <span>
                      Research shows that the earlier we identify speech,
                      behavioral, or developmental needs, the better the
                      outcomes. That’s why we focus on early screening, support,
                      and referrals during the most critical years of a child’s
                      development.
                    </span>
                  </p>
                  <p className="mt-3">
                    <strong>
                      <span className="me-2 text-primary">✔</span> Empowered
                      Families and Providers:
                    </strong>
                    <br />
                    <span>
                      We unite parents, caregivers, teachers, and medical
                      professionals through our platform, providing AI-guided
                      tools, training resources, and licensed providers to
                      ensure no one falls through the cracks.
                    </span>
                  </p>
                  <p className="mt-3">
                    <strong>
                      <span className="me-2 text-primary">✔</span> Equity,
                      Access, and Innovation:
                    </strong>
                    <br />
                    <span>
                      Every child—regardless of zip code, language, or
                      income—deserves a chance to thrive. Our platform ensures
                      access to culturally responsive tools,
                      Medicaid/Medicare-integrated services, and a growing
                      provider network tailored to local needs.
                    </span>
                  </p>

                  <div className="mt-5">
                    <div className="bg-primary-heading-main font-weight-semibold font-size-18 text-capitalize p-1 rounded-pill d-inline-block text-white pe-4">
                      <span className="bg-yellow d-inline-block p-1 bg-primary-heading-main rounded-pill favicon-logo01 text-center me-2">
                        <img
                          src="/assets/images/favicon-logo01.svg"
                          alt="..."
                          className="rounded-circle"
                        />
                      </span>
                      How We Work
                    </div>
                  </div>
                  <p className="mt-3">
                    <strong>
                      <span className="me-2 text-primary">✔</span>{" "}
                      Technology-Driven Solutions:
                    </strong>
                    <br />
                    <span>
                      Our AI tools help screen and match children with the right
                      providers and services.
                    </span>
                  </p>
                  <p className="mt-3">
                    <strong>
                      <span className="me-2 text-primary">✔</span> Real-Time
                      Support:
                    </strong>
                    <br />
                    <span>
                      We offer virtual consults, on-demand educational webinars,
                      and resource kits for both parents and professionals.
                    </span>
                  </p>
                  <p className="mt-3">
                    <strong>
                      <span className="me-2 text-primary">✔</span> Provided
                      Provider Network:
                    </strong>
                    <br />
                    <span>
                      Every provider in our system is vetted, licensed, and
                      aligned with best practices in early intervention.
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section> */}

        {/* {workerAllPlansData.length === 0 ? (
          <div className="text-center py-5">
            <h5 className="text-muted">No Plan Available</h5>
          </div>
        ) : (
          <div className="container mb-5">
            <div className="membership-plans">
              {workerAllPlansData.map((plan) => (
                <div className="plan-card active" key={plan.id}>
                  <div className="plan-header">
                    <h3 className="plan-title">{plan.name}</h3>
                  </div>

                  <div className="plan-price">
                    <strong>${plan.price} / month</strong>
                  </div>

                  <div className="plan-description">
                    <ul>
                      <li>{plan.description}</li>
                    </ul>
                  </div>

                  <div className="plan-footer">
                    <button
                      className="plan-button outline"
                      disabled={loadingPlanId === plan.id}
                      onClick={() =>
                        handlePlanPayment({
                          plan_id: plan.id,
                        })
                      }
                    >
                      {loadingPlanId === plan.id ? (
                        <span className="spinner-border spinner-border-sm me-2"></span>
                      ) : null}

                      {loadingPlanId === plan.id ? "Redirecting..." : "Pay"}
                    </button>

                  </div>
                </div>
              ))}
            </div>
          </div>
        )} */}

        {/* worker plan cards  */}

        {workerAllPlansData.length === 0 ? (
          <div className="text-center py-5">
            <h5 className="text-muted">No Plan Available</h5>
          </div>
        ) : (
          <div className="container-fluid mt-5 px-3">
            <div className="pricing-grid">
              {/* LEFT STATIC CARD */}
              <div className="pricing-card card-left">
                <div className="card-section-head">
                  <h6>BUILT FOR TODAY'S PROVIDERS</h6>
                  <div className="head-divider" />
                </div>
                <ul className="feat-list">
                  {features.map((item, i) => (
                    <li key={i}>
                      <div className="feat-title-row">
                        <div className="feat-icon-circle">
                          {item.icon}
                        </div>
                        {item.title}
                      </div>
                      <div className="feat-desc">{item.desc}</div>
                    </li>
                  ))}
                </ul>
                <div className="disclaimer-box">
                  We provide tools and reference-based guidance. Licensing and
                  regulatory determinations remain with state authorities.
                </div>
              </div>

              {/* DYNAMIC PLAN CARDS */}
              {workerAllPlansData.map((plan, index) => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(plan.description, "text/html");

                const items = [...doc.querySelectorAll("li")].map(li => li.innerHTML);
                const themes = [
                  {
                    cls: "plan-green",
                    color: "#168B46",
                    iconBg: "#E7F7EF",
                    checkColor: "#168B46",
                    label: "Get Free Snapshot",
                    prefix: "↗ ",
                  },
                  {
                    cls: "plan-blue",
                    color: "#0366CA",
                    iconBg: "#E6F1FB",
                    checkColor: "#0366CA",
                    label: "Start Onboarding",
                    prefix: "",
                  },
                  {
                    cls: "plan-purple",
                    color: "#5936BC",
                    iconBg: "#EEE9FB",
                    checkColor: "#5936BC",
                    label: "Access Resource Database",
                    prefix: "",
                  },
                ];
                const t = themes[index] || themes[0];

                return (
                  <div
                    className={`pricing-card plan-card ${t.cls}`}
                    key={plan.id}
                  >
                    <div className="plan-header">
                      <div className="plan-icon-wrap">
                        {/* You can swap this with plan-specific SVG icons */}
                        <svg
                          width="26"
                          height="26"
                          viewBox="0 0 26 26"
                          fill="none"
                        >
                          <circle
                            cx="13"
                            cy="10"
                            r="5"
                            stroke={t.color}
                            strokeWidth="1.5"
                          />
                          <path
                            d="M3 23c0-5 4.5-9 10-9s10 4 10 9"
                            stroke={t.color}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>
                      <p className="plan-name">{plan.name}</p>
                      {/* <p className="plan-tagline">{plan.description}</p> */}
                    </div>
                    <div className="plan-header-divider">
                      <div className="divider-line" />
                      <div className="divider-dot" />
                      <div className="divider-line" />
                    </div>
                    <div className="plan-body">
                      <ul className="feature-list">
                        {items.map((item, i) => (
                          <li key={i}>
                            <span className="feat-check">
                              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                                <path
                                  d="M1 4l2 2 4-3"
                                  stroke={t.checkColor}
                                  strokeWidth="1.2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </span>

                            <span dangerouslySetInnerHTML={{ __html: item }} />
                          </li>
                        ))}
                      </ul>
                      <div className="price-block">
                        <div
                          className="price-amount"
                          style={{ color: t.color }}
                        >
                          ${plan.price}
                        </div>

                        {index === 0 && (
                          <div
                            className="price-period"
                            style={{ color: t.color, fontWeight: 600 }}
                          >
                            Always Free
                          </div>
                        )}
                        {index === 1 && (
                          <>
                            <div
                              className="price-period"
                              style={{ color: t.color }}
                            >
                              per center / month
                            </div>
                            <div
                              className="price-annual"
                              style={{ color: t.color, opacity: 0.8 }}
                            >
                              or $499 annually
                            </div>
                          </>
                        )}
                        {index === 2 && (
                          <>
                            <div
                              className="price-period"
                              style={{ color: t.color }}
                            >
                              per center / month
                            </div>
                            <div
                              className="price-annual"
                              style={{ color: t.color, opacity: 0.8 }}
                            >
                              or $799 annually
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        className="plan-cta-btn"
                        style={{ backgroundColor: t.color }}
                        disabled={loadingPlanId === plan.id}
                        onClick={() => handlePlanPayment({ plan_id: plan.id })}
                      >
                        {loadingPlanId === plan.id ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Redirecting...
                          </>
                        ) : (
                          `${t.prefix}${t.label}`
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* RIGHT STATIC CARD */}
              <div
                className="pricing-card card-right"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <div className="card-section-head">
                  <h6>GOVTECH GRADE BY DESIGN</h6>
                  <div className="head-divider" />
                </div>
                <ul className="feat-list" style={{ flex: 1 }}>
                  {[
                    {
                      icon: "🔒",
                      title: "Secure & Encrypted",
                      desc: "Your data is encrypted in transit and at rest.",
                    },
                    {
                      icon: "☁",
                      title: "Privacy First",
                      desc: "We never sell your data. Ever.",
                    },
                    {
                      icon: "🏛",
                      title: "Nonprofit Mission",
                      desc: "100% mission-aligned. No shareholder markup.",
                    },
                    {
                      icon: "⭐",
                      title: "Provider Focused",
                      desc: "Built with input from directors, teachers, and families.",
                    },
                  ].map((item, i) => (
                    <li key={i}>
                      <div className="feat-title-row">
                        <span style={{ fontSize: 14 }}>{item.icon}</span>
                        {item.title}
                      </div>
                      <div className="feat-desc">{item.desc}</div>
                    </li>
                  ))}
                </ul>
                <div className="help-box">
                  <div style={{ fontSize: 18 }}>🤖</div>
                  <h4>Need Help Choosing?</h4>
                  <p>
                    Our team is here to help you find the right plan.{" "}
                    <span
                      style={{ textDecoration: "underline", cursor: "pointer" }}
                    >
                      Contact Us →
                    </span>
                  </p>
                </div>
                <button className="contact-btn">Contact Us →</button>
              </div>
            </div>
          </div>
        )}

        {/* four horizontal cards */}

        <section className="ncaf-trust-section">
          <div className="ncaf-trust-grid">
            <div className="trust-card">
              <div className="trust-icon-wrap">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <path
                    d="M14 2L3 7v8c0 6.6 4.7 12.8 11 14.4C20.3 27.8 25 21.6 25 15V7L14 2z"
                    stroke="#0B3C8C"
                    strokeWidth="1.8"
                    fill="none"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 14l3.5 3.5L19 10"
                    stroke="#0B3C8C"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="trust-content">
                <h4 className="trust-title">Trusted by Providers</h4>
                <p className="trust-desc">
                  Built with real-world insights from childcare leaders
                  nationwide.
                </p>
              </div>
            </div>

            <div className="trust-card">
              <div className="trust-icon-wrap">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <circle
                    cx="14"
                    cy="10"
                    r="5"
                    stroke="#0B3C8C"
                    strokeWidth="1.8"
                  />
                  <circle
                    cx="6"
                    cy="12"
                    r="3.5"
                    stroke="#0B3C8C"
                    strokeWidth="1.5"
                  />
                  <circle
                    cx="22"
                    cy="12"
                    r="3.5"
                    stroke="#0B3C8C"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M1 24c0-3.3 2.2-5 5-5"
                    stroke="#0B3C8C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M27 24c0-3.3-2.2-5-5-5"
                    stroke="#0B3C8C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                  <path
                    d="M5 24c0-4.4 4-8 9-8s9 3.6 9 8"
                    stroke="#0B3C8C"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div className="trust-content">
                <h4 className="trust-title">Scalable by Design</h4>
                <p className="trust-desc">
                  From single centers to multi-site organizations.
                </p>
              </div>
            </div>

            <div className="trust-card">
              <div className="trust-icon-wrap">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect
                    x="3"
                    y="14"
                    width="5"
                    height="10"
                    rx="1"
                    stroke="#0B3C8C"
                    strokeWidth="1.8"
                  />
                  <rect
                    x="11"
                    y="9"
                    width="5"
                    height="15"
                    rx="1"
                    stroke="#0B3C8C"
                    strokeWidth="1.8"
                  />
                  <rect
                    x="19"
                    y="4"
                    width="5"
                    height="20"
                    rx="1"
                    stroke="#0B3C8C"
                    strokeWidth="1.8"
                  />
                </svg>
              </div>
              <div className="trust-content">
                <h4 className="trust-title">Data-Informed Decisions</h4>
                <p className="trust-desc">
                  Real-time visibility to help you lead with confidence.
                </p>
              </div>
            </div>

            <div className="trust-card">
              <div className="trust-icon-wrap">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <polygon
                    points="14,2 17.5,10 26,10.5 19.5,16.5 21.5,25 14,20.5 6.5,25 8.5,16.5 2,10.5 10.5,10"
                    stroke="#0B3C8C"
                    strokeWidth="1.8"
                    fill="none"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="trust-content">
                <h4 className="trust-title">Advancing Equity</h4>
                <p className="trust-desc">
                  Supporting every community with access, clarity, and
                  opportunity.
                </p>
              </div>
            </div>
          </div>
        </section>

        <WorkerFooter />
      </div>
      {isRedirecting && (
        <div className="fullscreen-loader">
          <div className="loader-box">
            <div className="spinner-border text-light mb-3" />
            <p className="text-white mb-0">Redirecting to secure payment...</p>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkerAllPlans;
